import React, { useState, useEffect } from 'react';
import { Route, Switch, Redirect, useHistory } from "react-router-dom";
import { Routes } from "../routes";

// pages
import Presentation from "./Presentation";
import Upgrade from "./Upgrade";
import DashboardOverview from "./dashboard/DashboardOverview";
import RapportJournalier from "./rapport/RapportJournalier";
import RapportGeneral from "./rapport/RapportGeneral";
import RapportFichesVendues from "./rapport/RapportFichesVendues";
import RapportFichesGagnantes from "./rapport/RapportFichesGagnantes";
import RapportFichesEliminees from "./rapport/RapportFichesEliminees";
import AgentsPOS from './agents/AgentsPOS.js';
import Branches from './dashboard/branches.jsx';
import Statistiques from "./surveillance/Statistiques.js";
import BlocageBoule from "./dashboard/blocage_boule.jsx";
import LimiteBoule from "./dashboard/limite_boule.jsx";
import Limites from "./surveillance/Limites";
import LotsGagnant from "./surveillance/LotsGagnant.js";
import Tracabilite from "./surveillance/Tracabilite.js";
import MariageGratuit from './configurations/MariageGratuit.js'; // free marriage configs
import Tirages from './configurations/Tirages.js';
import Primes from './configurations/Primes.js';
import Transactions from "./Transactions";
import Settings from "./Settings";
import BootstrapTables from "./tables/BootstrapTables";
import Signin from "./examples/Signin";
import Signup from "./examples/Signup";
import ForgotPassword from "./examples/ForgotPassword";
import ResetPassword from "./examples/ResetPassword";
import Lock from "./examples/Lock";
import NotFoundPage from "./examples/NotFound";
import ServerError from "./examples/ServerError";

// documentation pages
import DocsOverview from "./documentation/DocsOverview";
import DocsDownload from "./documentation/DocsDownload";
import DocsQuickStart from "./documentation/DocsQuickStart";
import DocsLicense from "./documentation/DocsLicense";
import DocsFolderStructure from "./documentation/DocsFolderStructure";
import DocsBuild from "./documentation/DocsBuild";
import DocsChangelog from "./documentation/DocsChangelog";

// components
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Preloader from "../components/Preloader";
import Accordion from "./components/Accordion";
import Alerts from "./components/Alerts";
import Badges from "./components/Badges";
import Breadcrumbs from "./components/Breadcrumbs";
import Buttons from "./components/Buttons";
import Forms from "./components/Forms";
import Modals from "./components/Modals";
import Navs from "./components/Navs";
import Navbars from "./components/Navbars";
import Pagination from "./components/Pagination";
import Popovers from "./components/Popovers";
import Progress from "./components/Progress";
import Tables from "./components/Tables";
import Tabs from "./components/Tabs";
import Tooltips from "./components/Tooltips";
import Toasts from "./components/Toasts";

import { canAccessPath, getDefaultLandingPath, getStoredUser } from "../utils/access";
import { initSessionGuard, takeTabControl, redirectToSignin } from "../utils/sessionGuard";
import { Button } from '@themesberg/react-bootstrap';

const RouteWithLoader = ({ component: Component, ...rest }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Route {...rest} render={props => ( <> <Preloader show={loaded ? false : true} /> <Component {...props} /> </> ) } />
  );
};

const AuthenticatedShell = ({ Component, routeProps, sidebarCollapsed, setSidebarCollapsed, loaded }) => {
  const history = useHistory();
  const [isLeader, setIsLeader] = useState(true);

  useEffect(() => {
    return initSessionGuard({
      onLeaderChange: setIsLeader,
      onRevoked: () => redirectToSignin(history),
    });
  }, [history]);

  return (
    <>
      <Preloader show={loaded ? false : true} />
      <Sidebar collapsed={sidebarCollapsed} />
      <main className={`content ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <Navbar toggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} />
        {isLeader ? (
          <Component {...routeProps} />
        ) : (
          <div className="d-flex flex-column align-items-center justify-content-center text-center p-5" style={{ minHeight: '60vh' }}>
            <h4 className="mb-3">Cet onglet est en veille</h4>
            <p className="text-muted mb-4" style={{ maxWidth: 480 }}>
              Le tableau de bord est déjà ouvert dans un autre onglet.
              Un seul onglet reste actif pour éviter de surcharger le serveur.
            </p>
            <Button
              variant="primary"
              onClick={() => {
                takeTabControl();
                setIsLeader(true);
              }}
            >
              Prendre le contrôle ici
            </Button>
          </div>
        )}
        <Footer />
      </main>
    </>
  );
};

const RouteWithSidebar = ({ component: Component, ...rest }) => {
  const [loaded, setLoaded] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Route {...rest} render={props => {
      const token = localStorage.getItem("access_token");
      if (!token) {
        return <Redirect to={Routes.Signin.path} />;
      }
      const user = getStoredUser();
      if (user && !canAccessPath(props.match.path, user)) {
        return <Redirect to={getDefaultLandingPath(user)} />;
      }
      return (
        <AuthenticatedShell
          Component={Component}
          routeProps={props}
          sidebarCollapsed={sidebarCollapsed}
          setSidebarCollapsed={setSidebarCollapsed}
          loaded={loaded}
        />
      );
    }}
    />
  );
};



export default () => (
  <Switch>
    <RouteWithLoader exact path="/" component={Signin} />
    <RouteWithLoader exact path={Routes.Signin.path} component={Signin} />

    <RouteWithLoader exact path={Routes.Signup.path} component={Signup} />
    <RouteWithLoader exact path={Routes.ForgotPassword.path} component={ForgotPassword} />
    <RouteWithLoader exact path={Routes.ResetPassword.path} component={ResetPassword} />
    <RouteWithLoader exact path={Routes.Lock.path} component={Lock} />
    <RouteWithLoader exact path={Routes.NotFound.path} component={NotFoundPage} />
    <RouteWithLoader exact path={Routes.ServerError.path} component={ServerError} />

    {/* pages */}
    <RouteWithSidebar exact path={Routes.DashboardOverview.path} component={DashboardOverview} />
    <RouteWithSidebar exact path={Routes.RapportJournalier.path} component={RapportJournalier} />
    <RouteWithSidebar exact path={Routes.RapportGeneral.path} component={RapportGeneral} />
    <RouteWithSidebar exact path={Routes.RapportFichesVendues.path} component={RapportFichesVendues} />
    <RouteWithSidebar exact path={Routes.RapportFichesGagnantes.path} component={RapportFichesGagnantes} />
    <RouteWithSidebar exact path={Routes.RapportFichesEliminees.path} component={RapportFichesEliminees} />
    <RouteWithSidebar exact path={Routes.Agents.path} component={AgentsPOS} />
    <RouteWithSidebar exact path={Routes.Branches.path} component={Branches} />
    <RouteWithSidebar exact path={Routes.Statistiques.path} component={Statistiques} />
    <RouteWithSidebar exact path={Routes.BlocageBoule.path} component={BlocageBoule} />
    <RouteWithSidebar exact path={Routes.LimiteBoule.path} component={LimiteBoule} />
    <RouteWithSidebar exact path={Routes.Limites.path} component={Limites} />
    <RouteWithSidebar exact path={Routes.LotsGagnant.path} component={LotsGagnant} />
    <RouteWithSidebar exact path={Routes.Tracabilite.path} component={Tracabilite} />
    <RouteWithSidebar exact path={Routes.MariageGratuit.path} component={MariageGratuit} />
    <RouteWithSidebar exact path={Routes.Tirages.path} component={Tirages} />
    <RouteWithSidebar exact path={Routes.Primes.path} component={Primes} />
    <RouteWithSidebar exact path={Routes.Upgrade.path} component={Upgrade} />
    <RouteWithSidebar exact path={Routes.Transactions.path} component={Transactions} />
    <RouteWithSidebar exact path={Routes.Settings.path} component={Settings} />
    <RouteWithSidebar exact path={Routes.BootstrapTables.path} component={BootstrapTables} />

    {/* components */}
    <RouteWithSidebar exact path={Routes.Accordions.path} component={Accordion} />
    <RouteWithSidebar exact path={Routes.Alerts.path} component={Alerts} />
    <RouteWithSidebar exact path={Routes.Badges.path} component={Badges} />
    <RouteWithSidebar exact path={Routes.Breadcrumbs.path} component={Breadcrumbs} />
    <RouteWithSidebar exact path={Routes.Buttons.path} component={Buttons} />
    <RouteWithSidebar exact path={Routes.Forms.path} component={Forms} />
    <RouteWithSidebar exact path={Routes.Modals.path} component={Modals} />
    <RouteWithSidebar exact path={Routes.Navs.path} component={Navs} />
    <RouteWithSidebar exact path={Routes.Navbars.path} component={Navbars} />
    <RouteWithSidebar exact path={Routes.Pagination.path} component={Pagination} />
    <RouteWithSidebar exact path={Routes.Popovers.path} component={Popovers} />
    <RouteWithSidebar exact path={Routes.Progress.path} component={Progress} />
    <RouteWithSidebar exact path={Routes.Tables.path} component={Tables} />
    <RouteWithSidebar exact path={Routes.Tabs.path} component={Tabs} />
    <RouteWithSidebar exact path={Routes.Tooltips.path} component={Tooltips} />
    <RouteWithSidebar exact path={Routes.Toasts.path} component={Toasts} />

    {/* documentation */}
    <RouteWithSidebar exact path={Routes.DocsOverview.path} component={DocsOverview} />
    <RouteWithSidebar exact path={Routes.DocsDownload.path} component={DocsDownload} />
    <RouteWithSidebar exact path={Routes.DocsQuickStart.path} component={DocsQuickStart} />
    <RouteWithSidebar exact path={Routes.DocsLicense.path} component={DocsLicense} />
    <RouteWithSidebar exact path={Routes.DocsFolderStructure.path} component={DocsFolderStructure} />
    <RouteWithSidebar exact path={Routes.DocsBuild.path} component={DocsBuild} />
    <RouteWithSidebar exact path={Routes.DocsChangelog.path} component={DocsChangelog} />

    <Redirect to={Routes.NotFound.path} />
  </Switch>
);
